/**
 * SharedInputContext - Shared input handler context for multi-page documents
 * 
 * This provides a single input handler that is shared across all pages.
 * The VirtualPages component owns the input handler and keyboard listener.
 * Pages only use the input handler for rendering caret/selection and mouse events.
 */

import React, { createContext, useContext, useRef, useCallback, useState, useEffect } from 'react';
import { useEdgeDocsInput, EditCommand, EditCommandResult } from './EdgeDocsInput';
import { createTextEditingHandler } from './EdgeDocsCanvasFunctionsClassesObjects/input';
import { createKeyboardEventSetup } from './EdgeDocsCanvasFunctionsClassesObjects/event';
import type { SelectionRange } from './EdgeDocsTypes';
import type { Line, CaretPosition, PageLayout, ParagraphSnapshot } from '../../core/types/Typesetter';
import { TypesetterWorkerClient } from '../../workers/TypesetterWorkerClient';
import { UndoManager, ParagraphFormattingChange } from './UndoManager';
import { InputLogger } from '../../../../utils/loggers/InputLogger';
import { WasmLogger } from '../../../../utils/loggers/WasmLogger';
// 🆕 Phase 4: Feature flag for O(log n) caret routing
import { isTargetedCaretRoutingEnabled } from '../../config/featureFlags';
// 🔧 REFACTOR (2026-01-16): Import shared types from single source of truth
import type { SharedInputHandler, SharedInputContextValue, IncrementalRenderCallback } from './types/SharedInputTypes';
// Re-export for backward compatibility
export type { SharedInputHandler, SharedInputContextValue, IncrementalRenderCallback } from './types/SharedInputTypes';

// ============================================================================
// DEBUG CONTROL
// ============================================================================
const DEBUG_ENABLED = false; // 🔧 DISABLED for production
const debugLog = DEBUG_ENABLED ? console.log.bind(console) : () => {};

// ============================================================================
// OPTION C: WASM as Single Source of Truth
// ============================================================================
const OPTION_C_ENABLED = true;

// ============================================================================
// NOTE: SharedInputHandler, SharedInputContextValue, and IncrementalRenderCallback
// are now defined in ./types/SharedInputTypes.ts (Single Source of Truth)
// and re-exported from this file for backward compatibility.
// ============================================================================

const SharedInputContext = createContext<SharedInputContextValue | null>(null);

// ============================================================================
// Hook to use shared input handler
// ============================================================================
export const useSharedInput = () => {
  const ctx = useContext(SharedInputContext);
  // Return null if not in provider - allows components to work standalone
  return ctx;
};

// ============================================================================
// Provider Props
// ============================================================================
interface SharedInputProviderProps {
  children: React.ReactNode;
  typesetterClient: TypesetterWorkerClient | null;
  documentState?: { text: string; pageSetup?: any; fontSettings?: any };
  onDocumentTextChange?: (newText: string) => void;
  onPageCountChange?: (pageCount: number) => void;
  // 🆕 2026-01-21: Character formatting callbacks for keyboard shortcuts (Ctrl+B, Ctrl+I)
  onToggleBold?: () => Promise<void>;
  onToggleItalic?: () => Promise<void>;
  // 🔧 FIX (2026-01-24): Selection change callback for toolbar formatting operations
  onSelectionChange?: (selection: {
    anchor: { logical?: { paraIndex: number; charOffset: number } };
    focus: { logical?: { paraIndex: number; charOffset: number } };
    isCollapsed: boolean;
  }) => void;
}

// ============================================================================
// Provider Component
// ============================================================================
export const SharedInputProvider: React.FC<SharedInputProviderProps> = ({
  children,
  typesetterClient,
  documentState,
  onDocumentTextChange,
  onPageCountChange,
  onToggleBold,       // 🆕 2026-01-21: Keyboard shortcut callback
  onToggleItalic,     // 🆕 2026-01-21: Keyboard shortcut callback
  onSelectionChange   // 🔧 FIX (2026-01-24): Selection change callback
}) => {
  // 🔬 DEBUG: Track instance identity to prove mount/unmount vs effect churn
  const instanceId = React.useRef(crypto.randomUUID().slice(0, 8));
  React.useEffect(() => {
    InputLogger.context.mounted({ instanceId: instanceId.current });
    InputLogger.context.init({ instanceId: instanceId.current });
    return () => {
      InputLogger.context.unmounted({ instanceId: instanceId.current });
    };
  }, []);
  // ============================================================================
  // Refs for input handler dependencies
  // ============================================================================
  const typesetterClientRef = useRef<TypesetterWorkerClient | null>(typesetterClient);
  const currentDocumentTextRef = useRef<string>(documentState?.text || '');
  const currentSnapshotRef = useRef<ParagraphSnapshot | null>(null);
  const lastSelectionRef = useRef<SelectionRange>({ anchor: 0, focus: 0 });
  const incrementalPagesRef = useRef<{
    pages: PageLayout[];
    totalPages: number;
    stoppedAtPage: number | null;
    timestamp: number;
  } | null>(null);
  const lastRenderWasIncrementalRef = useRef<boolean>(false);
  const forceReinitRef = useRef<boolean>(false);
  const consecutiveWasmErrorsRef = useRef<number>(0);
  const lastLocalTextUpdateRef = useRef<string | null>(null);
  const editIdCounterRef = useRef<number>(0); // 🔬 DUPLICATION TRACKING
  
  // 🚀 PHASE 4: Canvas render registry for incremental updates
  // Maps pageIndex → incremental render callback
  // This allows bypassing React reconciliation for performance-critical updates
  const canvasRenderersRef = useRef<Map<number, IncrementalRenderCallback>>(new Map());
  const overlaySettingsRef = useRef<{ enabled: boolean }>({ enabled: false });
  const renderOverlayRef = useRef<(() => void) | null>(null);
  const drawOverlaysRef = useRef<(() => void) | null>(null);
  
  // � FIX (2026-01-16): Caret update callback registry for multi-page support
  // Maps pageIndex → caret update callback
  // When cursor changes, ALL pages get notified and can check if cursor is on their page
  const caretUpdateCallbacksRef = useRef<Map<number, (cursorIndex: number) => void>>(new Map());
  
  // �🐛 DEBUG: Track last edit info for dashboard (delete/backspace monitoring)
  const lastEditInfoRef = useRef<{
    type: 'insert' | 'delete' | 'unknown';
    position: number;
    deleteCount: number;
    insertLength: number;
    deletedParagraphIds: string[];
    prevParagraphCount: number;
    newParagraphCount: number;
    timestamp: number;
  } | null>(null);
  
  // ============================================================================
  // State
  // ============================================================================
  const [stableInputText, setStableInputText] = useState<string>(documentState?.text || '');
  const [renderTrigger, setRenderTrigger] = useState(0);
  const [fontFamily, setFontFamily] = useState('Inter');
  const [fontSizePx, setFontSizePx] = useState(14);
  const [isReady, setIsReady] = useState(false);
  const [isWasmTextReady, setIsWasmTextReady] = useState(false);
  // 🆕 Undo/Redo state
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  // 🔥 PERFORMANCE FIX: Use ref to avoid stale closure in handleEditCommand
  // Without this ref, handleEditCommand captures isWasmTextReady at creation time
  // and never sees updates, causing "first successful edit" to log on EVERY keystroke
  const isWasmTextReadyRef = React.useRef(false);
  isWasmTextReadyRef.current = isWasmTextReady;
  
  // 🔬 DEBUG: Track isWasmTextReady changes
  React.useEffect(() => {
    InputLogger.context.wasmReady(isWasmTextReady, { instanceId: instanceId.current });
  }, [isWasmTextReady]);
  
  // Store fallback text for initial load (before WASM is ready)
  const fallbackTextRef = useRef<string>(documentState?.text || '');
  
  // ============================================================================
  // UndoManager Setup
  // ============================================================================
  // Ref to hold the inputHandler (set after useEdgeDocsInput)
  const inputHandlerRef = useRef<SharedInputHandler | null>(null);

  // 🆕 2026-01-21: Refs for formatting callbacks to avoid useEffect dependency churn
  // These refs are updated when props change, but the useEffect only runs once
  const onToggleBoldRef = useRef<(() => Promise<void>) | undefined>(onToggleBold);
  const onToggleItalicRef = useRef<(() => Promise<void>) | undefined>(onToggleItalic);

  // 🔧 FIX (2026-01-24): Ref for selection change callback to avoid useEffect dependency churn
  // This enables toolbar operations to track the last non-collapsed selection
  const onSelectionChangeRef = useRef<typeof onSelectionChange>(onSelectionChange);

  // Ref to hold the formatting updater (set by EdgeDocsVirtualPages)
  // This allows undo/redo of formatting changes to call back into the formatting pipeline
  const applyFormattingRef = useRef<((updates: Array<{ id: string; lineSpacing?: number; fontSizePx?: number; fontFamily?: string }>) => Promise<void>) | null>(null);

  // Create UndoManager instance (stable across renders)
  const undoManagerRef = useRef<UndoManager | null>(null);
  
  // Initialize UndoManager lazily (needs access to applyEdit which uses WASM)
  const getUndoManager = useCallback(() => {
    if (!undoManagerRef.current) {
      undoManagerRef.current = new UndoManager({
        applyEdit: async (position: number, deleteCount: number, insertText: string) => {
          // Apply edit via WASM - same path as normal edits
          const client = typesetterClientRef.current;
          if (!client) {
            console.warn('[UndoManager] TypesetterWorkerClient not available');
            return;
          }
          
          let command: EditCommand;
          if (deleteCount > 0 && insertText.length > 0) {
            command = { type: 'replace', position, deleteCount, text: insertText };
          } else if (deleteCount > 0) {
            command = { type: 'delete', position, deleteCount };
          } else {
            command = { type: 'insert', position, text: insertText };
          }
          
          // Apply via WASM (this triggers full layout pipeline)
          const result = await client.applyEditCommand({
            ...command,
            fontFamily,
            fontWeight: 400,
            fontStyle: 'normal'
          });
          
          if (result.success) {
            // Update refs and trigger render
            currentDocumentTextRef.current = result.newText;
            setStableInputText(result.newText);

            // 🔥 FIX 2026-01-20: Update incrementalPagesRef with fresh pagination data from WASM
            // Without this, undo/redo would render with stale page data, causing lines to disappear.
            // The applyEditCommand returns fresh updatedPages from WASM that have correct line ranges.
            //
            // 🔧 FIX 2026-01-20 (v2): Use merge logic instead of replace, same as normal edits.
            // Undo/redo may return partial page updates, so we need to merge with existing pages.
            if (result.updatedPages && result.updatedPages.length > 0) {
              const existingPages = incrementalPagesRef.current?.pages || [];

              // Build a map of existing pages by page_index for O(1) lookup
              const existingPagesMap = new Map<number, any>();
              for (const page of existingPages) {
                if (typeof page.page_index === 'number') {
                  existingPagesMap.set(page.page_index, page);
                }
              }

              // Merge new pages into the map (overwrite existing pages with same index)
              for (const page of result.updatedPages) {
                if (typeof page.page_index === 'number') {
                  existingPagesMap.set(page.page_index, page);
                }
              }

              // Remove pages beyond totalPages when document shrinks
              if (typeof result.totalPages === 'number') {
                for (const pageIndex of existingPagesMap.keys()) {
                  if (pageIndex >= result.totalPages) {
                    debugLog(`📱 [SharedInput:Undo] Removing stale page ${pageIndex} (totalPages now ${result.totalPages})`);
                    existingPagesMap.delete(pageIndex);
                  }
                }
              }

              // Convert map back to array, sorted by page_index
              const mergedPages = Array.from(existingPagesMap.values())
                .sort((a, b) => (a.page_index ?? 0) - (b.page_index ?? 0));

              incrementalPagesRef.current = {
                pages: mergedPages,
                totalPages: result.totalPages,
                stoppedAtPage: null,  // Full pagination, not stopped early
                timestamp: Date.now()
              };

              debugLog(`📱 [SharedInput:Undo] Merged pages: existing=${existingPages.length}, new=${result.updatedPages.length}, merged=${mergedPages.length}`);
            }

            setRenderTrigger(prev => prev + 1);

            // Notify parent
            onDocumentTextChange?.(result.newText);
          }
        },
        applyFormatting: async (updates) => {
          // Apply formatting via the registered formatting updater
          // This goes through the same pipeline as user-initiated formatting changes
          const applyFn = applyFormattingRef.current;
          if (!applyFn) {
            console.warn('[UndoManager] applyFormatting callback not registered');
            return;
          }

          debugLog(`📱 [SharedInput:Undo] Applying formatting to ${updates.length} paragraph(s)`, updates);
          await applyFn(updates);

          // Trigger render after formatting change
          setRenderTrigger(prev => prev + 1);
        },
        setCursor: (position: number) => {
          // Set cursor via input handler (using ref)
          const handler = inputHandlerRef.current;
          if (handler) {
            handler.setCursorPosition(position);
          }
        },
        getCursor: () => {
          return lastSelectionRef.current?.focus ?? 0;
        }
      });
    }
    return undoManagerRef.current;
  }, [fontFamily, onDocumentTextChange]);
  
  // Update undo/redo button state after each operation
  const updateUndoRedoState = useCallback(() => {
    const undoManager = undoManagerRef.current;
    if (undoManager) {
      setCanUndo(undoManager.canUndo());
      setCanRedo(undoManager.canRedo());
    }
  }, []);
  
  // Undo handler (exposed via context)
  const handleUndo = useCallback(async () => {
    const stackSizes = undoManagerRef.current?.getStackSizes?.();
    InputLogger.history.undo({ undoStackSize: stackSizes?.undo, redoStackSize: stackSizes?.redo });
    const undoManager = getUndoManager();
    await undoManager.undo();
    updateUndoRedoState();
  }, [getUndoManager, updateUndoRedoState]);
  
  // Redo handler (exposed via context)
  const handleRedo = useCallback(async () => {
    const stackSizes = undoManagerRef.current?.getStackSizes?.();
    InputLogger.history.redo({ undoStackSize: stackSizes?.undo, redoStackSize: stackSizes?.redo });
    const undoManager = getUndoManager();
    await undoManager.redo();
    updateUndoRedoState();
  }, [getUndoManager, updateUndoRedoState]);

  // Register formatting updater (called by EdgeDocsVirtualPages)
  // This allows undo/redo to call back into the formatting pipeline
  const registerFormattingUpdater = useCallback((
    updater: (updates: Array<{ id: string; lineSpacing?: number; fontSizePx?: number; fontFamily?: string }>) => Promise<void>
  ) => {
    applyFormattingRef.current = updater;
    debugLog(`📱 [SharedInput] Formatting updater registered`);
  }, []);

  // Record a formatting change for undo/redo
  // Called AFTER formatting has been applied, with before/after states
  const recordFormattingChange = useCallback((changes: ParagraphFormattingChange[]) => {
    const undoManager = getUndoManager();
    undoManager.recordFormatting(changes);
    updateUndoRedoState();
    debugLog(`📱 [SharedInput] Recorded formatting change for ${changes.length} paragraph(s)`);
  }, [getUndoManager, updateUndoRedoState]);

  // 🔬 DIAGNOSTIC: Expose refs to window for console debugging
  // This allows running multiParagraphDiagnostic.js to verify snapshot cache state
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__SHARED_INPUT_CONTEXT__ = {
        currentDocumentTextRef,
        cursorPositionRef: lastSelectionRef, // cursor is at focus position
        client: typesetterClientRef.current,
        incrementalPagesRef,
      };
      // Also expose client directly for easier access
      if (typesetterClientRef.current) {
        (window as any).__TYPESETTER_CLIENT__ = typesetterClientRef.current;
      }
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).__SHARED_INPUT_CONTEXT__;
        delete (window as any).__TYPESETTER_CLIENT__;
      }
    };
  }, [typesetterClient]);
  
  // Update typesetterClientRef when prop changes
  useEffect(() => {
    typesetterClientRef.current = typesetterClient;
    if (typesetterClient) {
      setIsReady(true);
      // Check if WASM already has text initialized
      // If WASM has any text, it's been initialized and we can trust it
      const wasmText = typesetterClient.getLastInitializedText();
      if (wasmText.length > 0) {
        setIsWasmTextReady(true);
        debugLog('📱 [SharedInput] WASM text ready on mount (has initialized text)');
      }
    }
  }, [typesetterClient]);
  
  // ============================================================================
  // OPTION A: Single Source of Truth - Authoritative Text Accessor
  // ============================================================================
  /**
   * Returns the authoritative document text.
   * - If WASM is ready: returns WASM's lastInitializedText (the single source of truth)
   * - If WASM not ready: returns the fallback props text (for initial render only)
   * 
   * ALL text reads in the system should go through this function.
   */
  // 🔥 PERFORMANCE FIX: Use ref to avoid callback identity churn
  // Without this, getAuthoritativeText is recreated when isWasmTextReady changes,
  // which causes contextValue to be recreated, triggering canvas unregister/register storm
  const getAuthoritativeText = useCallback((): string => {
    const client = typesetterClientRef.current;
    
    if (client && isWasmTextReadyRef.current) {
      const wasmText = client.getLastInitializedText();
      return wasmText;
    }
    
    // Fallback: WASM not ready yet, use props text
    // This should only happen during initial load
    return fallbackTextRef.current;
  }, []); // Empty deps - uses ref for current value
  
  /**
   * Called by Canvas after successful document initialization in WASM.
   * Marks WASM as the authoritative source for text.
   */
  // 🔥 PERFORMANCE FIX: Use ref to avoid callback identity churn
  // Without this, markWasmTextReady is recreated when isWasmTextReady changes,
  // which causes contextValue to be recreated, triggering canvas unregister/register storm
  const markWasmTextReady = useCallback(() => {
    if (!isWasmTextReadyRef.current) {
      setIsWasmTextReady(true);
      debugLog('📱 [SharedInput] WASM text marked as ready (document initialized)');
    }
  }, []); // Empty deps - uses ref for current value
  
  // Update document text ref AND stableInputText when prop changes
  // ✅ FIX: Must also update stableInputText so the input handler syncs its text state
  // Without this, the handler's text remains empty and cursor clicks don't work
  useEffect(() => {
    if (documentState?.text !== undefined && documentState?.text !== null) {
      // ✅ OPTION A: Update fallback text for initial load
      // This is only used before WASM is ready
      if (!isWasmTextReady) {
        fallbackTextRef.current = documentState.text;
      }
      
      // Always update currentDocumentTextRef (for backward compatibility during transition)
      if (documentState.text !== lastLocalTextUpdateRef.current) {
        const oldText = currentDocumentTextRef.current || '';
        const newText = documentState.text;
        
        // 🔬 DIAGNOSTIC: Track text state changes from documentState prop
        if (oldText !== newText) {
          InputLogger.sharedInput.textStateChanged(oldText.length, newText.length, 'documentState prop', {
            delta: newText.length - oldText.length,
            oldFormFeeds: (oldText.match(/\f/g) || []).length,
            newFormFeeds: (newText.match(/\f/g) || []).length,
            isWasmTextReady,
            timestamp: Date.now()
          });
        }
        
        currentDocumentTextRef.current = documentState.text;
      }
      
      // ✅ FIX: Also update stableInputText to sync handler's text state
      // This ensures the input handler knows about the loaded document text
      // The useEffect in useEdgeDocsInput will then sync cursor position correctly
      setStableInputText(prev => {
        if (prev !== documentState.text && documentState.text !== lastLocalTextUpdateRef.current) {
          debugLog('📱 [SharedInput] Syncing stableInputText from documentState:', {
            oldLength: prev.length,
            newLength: documentState.text.length
          });
          return documentState.text;
        }
        return prev;
      });
    }
  }, [documentState?.text, isWasmTextReady]);
  
  // Update font settings
  useEffect(() => {
    const fontSettings = documentState?.fontSettings;
    if (fontSettings?.fontFamily) {
      setFontFamily(fontSettings.fontFamily.replace(/'/g, ''));
    }
    if (fontSettings?.fontSizePx) {
      setFontSizePx(fontSettings.fontSizePx);
    }
  }, [documentState?.fontSettings]);
  
  // ============================================================================
  // Option C: Edit command handler
  // ============================================================================
  const handleEditCommand = useCallback(async (command: EditCommand): Promise<EditCommandResult> => {
    const t0 = performance.now();
    
    // 🔬 DUPLICATION TRACKING: Increment editId and log UI_RECEIVED
    editIdCounterRef.current += 1;
    const editId = editIdCounterRef.current;
    const insertText = command.type === 'insert' ? (command.text || '') : '';
    InputLogger.sharedInput.editReceived(editId, command.type, command.position ?? 0, insertText.length, insertText);
    
    debugLog('📱 [SharedInput] handleEditCommand called:', command);
    
    // 🔍 DIAGNOSTIC: Log text state BEFORE the edit
    const textBefore = currentDocumentTextRef.current || '';
    if (command.type === 'delete') {
      const deletePos = command.position;
      const deleteCount = command.deleteCount || 1;
      // Show characters around the delete position
      const charAtPos = textBefore.charAt(deletePos);
      const charBefore = deletePos > 0 ? textBefore.charAt(deletePos - 1) : '(start)';
      const charAfter = deletePos < textBefore.length - 1 ? textBefore.charAt(deletePos + 1) : '(end)';
      
      // Identify character types
      const charTypeAtPos = charAtPos === '\f' ? 'FORM_FEED' : 
                            charAtPos === '\n' ? 'NEWLINE' : 
                            charAtPos === ' ' ? 'SPACE' : `'${charAtPos}'`;
      const charTypeBefore = charBefore === '\f' ? 'FORM_FEED' : 
                             charBefore === '\n' ? 'NEWLINE' :
                             charBefore === ' ' ? 'SPACE' : 
                             charBefore === '(start)' ? '(start)' : `'${charBefore}'`;
      const charTypeAfter = charAfter === '\f' ? 'FORM_FEED' : 
                            charAfter === '\n' ? 'NEWLINE' :
                            charAfter === ' ' ? 'SPACE' : 
                            charAfter === '(end)' ? '(end)' : `'${charAfter}'`;
      
      // Count form feeds before and after delete position
      const formFeedsBefore = (textBefore.slice(0, deletePos).match(/\f/g) || []).length;
      const formFeedsAfter = (textBefore.slice(deletePos).match(/\f/g) || []).length;
      
      InputLogger.sharedInput.deletePreState({
        textLength: textBefore.length,
        deletePosition: deletePos,
        deleteCount,
        charBeingDeleted: charAtPos,
        charCodeBeingDeleted: charAtPos.charCodeAt(0),
        formFeedsBefore,
        formFeedsAfter,
        willDeleteFormFeed: charAtPos === '\f'
      });
    }
    
    const client = typesetterClientRef.current;
    if (!client) {
      console.warn('[SharedInput Option C] TypesetterWorkerClient not available');
      return {
        success: false,
        newText: currentDocumentTextRef.current || '',
        newCursorPosition: 0,
        error: 'TypesetterWorkerClient not available'
      };
    }
    
    try {
      // 🐛 DEBUG: Capture paragraph count BEFORE the edit for delta tracking
      const prevParagraphCount = client.getCachedParagraphSnapshots()?.length ?? 0;
      
      const tWasmStart = performance.now();
      const result = await client.applyEditCommand({
        ...command,
        fontFamily,
        fontWeight: 400,
        fontStyle: 'normal'
      });
      const tWasmEnd = performance.now();
      WasmLogger.timing('WASM applyEditCommand', tWasmEnd - tWasmStart);
      
      // 🔬 DUPLICATION TRACKING: Log WASM_RETURN
      const oldLen = textBefore.length;
      const newLen = result.newText.length;
      const newTail = result.newText.slice(-20).replace(/\n/g, '\\n');
      WasmLogger.phase3a.debug(`WASM_RETURN editId=${editId}`, { oldLen, newLen, delta: newLen - oldLen, newTail });
      
      if (result.success) {
        // 🐛 DEBUG: Update lastEditInfoRef for dashboard monitoring
        const newParagraphCount = client.getCachedParagraphSnapshots()?.length ?? 0;
        lastEditInfoRef.current = {
          type: command.type === 'insert' ? 'insert' : command.type === 'delete' ? 'delete' : 'unknown',
          position: command.position ?? 0,
          deleteCount: command.deleteCount ?? 0,
          insertLength: command.text?.length ?? 0,
          deletedParagraphIds: result.deletedParagraphIds || [],
          prevParagraphCount,
          newParagraphCount,
          timestamp: Date.now()
        };
        
        // 🔍 DIAGNOSTIC: Log text state AFTER the edit
        if (command.type === 'delete') {
          const textAfter = result.newText;
          const formFeedsNow = (textAfter.match(/\f/g) || []).length;
          const formFeedsBefore = (textBefore.match(/\f/g) || []).length;
          
          WasmLogger.phase3a.debug('DELETE DIAGNOSTIC Post-delete state', {
            textLengthBefore: textBefore.length,
            textLengthAfter: textAfter.length,
            charsDeleted: textBefore.length - textAfter.length,
            formFeedsBefore,
            formFeedsAfter: formFeedsNow,
            formFeedsRemoved: formFeedsBefore - formFeedsNow,
            pageBreakDeleted: formFeedsBefore > formFeedsNow,
            totalPagesNow: result.totalPages,
            textPreview: JSON.stringify(textAfter.slice(0, 30))
          });
          
          if (formFeedsBefore > formFeedsNow) {
            WasmLogger.cacheWarning('PAGE BREAK WAS DELETED! This will reduce page count.', {});
          }
        }
        
        // 🔬 DIAGNOSTIC: Track text state changes from WASM response
        const oldText = currentDocumentTextRef.current || '';
        WasmLogger.phase3a.debug('TEXT TRACKING WASM edit response', {
          source: 'WASM applyEditCommand',
          commandType: command.type,
          commandPosition: command.position,
          oldLength: oldText.length,
          newLength: result.newText.length,
          delta: result.newText.length - oldText.length,
          oldFormFeeds: (oldText.match(/\f/g) || []).length,
          newFormFeeds: (result.newText.match(/\f/g) || []).length,
          wasmReportedCursor: result.newCursorPosition,
          timestamp: Date.now()
        });
        
        // 🔬 TARGETED INSTRUMENTATION: Log snapshot details after Enter key
        // This helps diagnose caret resolution failures on empty paragraphs
        const insertedText = command.type === 'insert' ? (command.text || '') : '';
        const isEnterKey = insertedText === '\n\n' || insertedText === '\n';
        if (isEnterKey && result.updatedPages && result.updatedPages.length > 0) {
          const expectedCaretIndex = (command.position ?? 0) + insertedText.length;
          const page0 = result.updatedPages.find((p: any) => p.page_index === 0);
          
          const paragraphSlices = page0 ? (page0.paragraphs || []).map((p: any, i: number) => ({
            idx: i,
            id: p.paragraph_id?.slice(0, 8) || 'unknown',
            line_start: p.line_start,
            line_end: p.line_end,
          })) : undefined;
          
          InputLogger.sharedInput.enterDebug(
            command.position ?? 0,
            insertedText,
            expectedCaretIndex,
            paragraphSlices,
            {
              wasmReportedCursor: result.newCursorPosition,
              totalPages: result.totalPages,
              updatedPagesCount: result.updatedPages.length,
            }
          );
        }
        
        // Update local refs with WASM response
        currentDocumentTextRef.current = result.newText;
        lastLocalTextUpdateRef.current = result.newText;
        
        // 🔬 DUPLICATION TRACKING: Log TS_COMMIT
        const committedLen = currentDocumentTextRef.current.length;
        const committedTail = currentDocumentTextRef.current.slice(-20);
        InputLogger.sharedInput.tsCommit(editId, committedLen, committedTail);
        
        // 🔬 ROOT CAUSE DIAGNOSTIC: Check what WASM returned for pages
        // This helps identify if stale lines issue is due to:
        // 1. WASM not returning .lines on pages (only .paragraphs slices)
        // 2. WASM not returning all affected pages after DELETE
        // 3. Lines accumulating from multiple sources
        InputLogger.sharedInput.wasmUpdatedPages(
          editId,
          command.type,
          result.updatedPages?.map((p: any) => p.page_index) ?? [],
          result.updatedPages?.map((p: any) => ({
            pageIndex: p.page_index,
            hasLines: !!p.lines,
            linesCount: p.lines?.length ?? 0
          })) ?? [],
          {
            totalPages: result.totalPages,
            existingPagesCount: incrementalPagesRef.current?.pages?.length ?? 0,
            existingPageIndices: incrementalPagesRef.current?.pages?.map((p: any) => p.page_index) ?? []
          }
        );
        
        // 🔬 ROOT CAUSE: Log existing pages' .lines status BEFORE merge
        if (incrementalPagesRef.current?.pages) {
          InputLogger.sharedInput.existingPagesBeforeMerge(
            incrementalPagesRef.current.pages.map((p: any) => ({
              pageIndex: p.page_index,
              hasLines: !!p.lines,
              linesCount: p.lines?.length ?? 0
            }))
          );
        }
        
        // ✅ OPTION A: Mark WASM as text-ready after first successful edit
        // This confirms WASM has the authoritative text state
        // 🔥 PERFORMANCE FIX: Use ref to avoid stale closure (isWasmTextReady captured at callback creation)
        if (!isWasmTextReadyRef.current) {
          setIsWasmTextReady(true);
          debugLog('📱 [SharedInput] WASM text now ready (first successful edit)');
        }
        
        // Trigger re-render with new pages
        // 🔥 CRITICAL FIX: MERGE new pages into existing incrementalPagesRef instead of replacing.
        // When WASM returns a windowed pagination result (e.g., only pages [2,3] after editing on page 3),
        // replacing all pages would lose the slice bounds for pages 0 and 1. This causes those pages
        // to fall back to stale cached data with wrong slice bounds, rendering all content on one page.
        if (result.updatedPages.length > 0) {
          const existingPages = incrementalPagesRef.current?.pages || [];
          
          // Build a map of existing pages by page_index for O(1) lookup
          const existingPagesMap = new Map<number, any>();
          for (const page of existingPages) {
            if (typeof page.page_index === 'number') {
              existingPagesMap.set(page.page_index, page);
            }
          }
          
          // Merge new pages into the map (overwrite existing pages with same index)
          for (const page of result.updatedPages) {
            if (typeof page.page_index === 'number') {
              existingPagesMap.set(page.page_index, page);
            }
          }
          
          // 🔥 FIX (2026-01-12): Remove pages beyond totalPages when document shrinks.
          // When DELETE removes content, totalPages may decrease. Stale pages beyond
          // the new totalPages have invalid grapheme indices (pointing to deleted content)
          // and must be removed to prevent DUPLICATE_LINE_RANGES errors.
          if (typeof result.totalPages === 'number') {
            for (const pageIndex of existingPagesMap.keys()) {
              if (pageIndex >= result.totalPages) {
                debugLog(`📱 [SharedInput] Removing stale page ${pageIndex} (totalPages now ${result.totalPages})`);
                existingPagesMap.delete(pageIndex);
              }
            }
          }
          
          // Convert map back to array, sorted by page_index
          const mergedPages = Array.from(existingPagesMap.values())
            .sort((a, b) => (a.page_index ?? 0) - (b.page_index ?? 0));
          
          debugLog(`📱 [SharedInput] Merged pages: existing=${existingPages.length}, new=${result.updatedPages.length}, merged=${mergedPages.length}`);
          
          // 🔬 ROOT CAUSE: Log merged pages' .lines status AFTER merge
          InputLogger.sharedInput.mergedPagesAfterMerge(
            editId,
            committedLen,
            result.totalPages,
            mergedPages.map((p: any) => ({
              pageIndex: p.page_index,
              hasLines: !!p.lines,
              linesCount: p.lines?.length ?? 0,
              maxGrapheme: p.lines?.length > 0 
                ? Math.max(...p.lines.map((l: any) => l.end_grapheme_index)) 
                : undefined,
              hasStaleLines: p.lines?.length > 0 
                ? p.lines.some((l: any) => l.end_grapheme_index > committedLen)
                : false
            }))
          );
          
          // 🔬 DEBUG: Log detailed line info for each merged page
          for (const page of mergedPages) {
            if (page.lines && page.lines.length > 0) {
              WasmLogger.snapshot.merge(`Page ${page.page_index} lines AFTER merge`, {
                linesCount: page.lines.length,
                lineRanges: page.lines.slice(0, 15).map((l: any) => `[${l.start_grapheme_index}-${l.end_grapheme_index})`).join(', ') + (page.lines.length > 15 ? '...' : ''),
                maxGrapheme: Math.max(...page.lines.map((l: any) => l.end_grapheme_index)),
                committedLen
              });
            }
          }
          
          incrementalPagesRef.current = {
            pages: mergedPages,
            totalPages: result.totalPages,
            stoppedAtPage: null,
            timestamp: Date.now()
          };
          lastRenderWasIncrementalRef.current = true;
          
          // Update page count
          if (onPageCountChange && result.totalPages) {
            onPageCountChange(result.totalPages);
          }
        }
        
        // 🚀 PHASE 4: Call canvas renderers directly (bypass React reconciliation)
        // This is the key performance optimization - only affected pages re-render
        // 
        // 🆕 WASM now returns affected_page_indices which tells us exactly which pages
        // have changed content (hash differs from previous). This is more accurate than
        // using all pages in updatedPages, which includes unchanged pages for context.
        const affectedPageIndices = new Set<number>();
        
        // 🔥 CRITICAL FIX: For DELETE operations, content shifts BACKWARD across pages.
        // WASM's affectedPageIndices only tells us which pages have changed hashes,
        // but subsequent pages have STALE slice boundaries (line_start/line_end).
        // We must re-render ALL pages from the affected page onward for deletes.
        const isDeleteOperation = command.type === 'delete';
        const totalRegisteredPages = canvasRenderersRef.current.size;
        
        // First, determine the minimum affected page from available data
        let minAffectedPage = 0;
        
        if (result.affectedPageIndices && result.affectedPageIndices.length > 0) {
          // Use WASM's affected page list
          result.affectedPageIndices.forEach((pageIndex: number) => {
            affectedPageIndices.add(pageIndex);
          });
          minAffectedPage = Math.min(...result.affectedPageIndices);
          debugLog('📱 [SharedInput] Using WASM affectedPageIndices:', result.affectedPageIndices);
        } else if (result.updatedPages && result.updatedPages.length > 0) {
          // 🔥 PERFORMANCE FIX (2026-01-10): For INSERT operations, only render the page
          // containing the edit, not ALL pages. WASM returns all pages in updatedPages for
          // context (slice bounds), but only the edited page actually needs re-rendering.
          // DELETE operations still need to re-render all subsequent pages (handled below).
          // 
          // 🔧 FIX (2026-01-16): EXCEPTION for page break insertion (\f\n from Ctrl+Enter)
          // When a page break is inserted, we MUST render ALL affected pages because:
          // 1. WASM creates two paragraphs/pages at the \f boundary
          // 2. The new page (page N+1) needs its canvas rendered for cursor positioning
          // 3. Without this, cursor can't position on the new page after Ctrl+Enter
          const insertedText = command.text ?? '';
          const isPageBreakInsert = insertedText.includes('\f');
          
          if (!isDeleteOperation && !isPageBreakInsert) {
            // For regular INSERT (not page break): Find the page containing the edit position
            // The edit position is command.position (grapheme index)
            // We can determine the page by finding which page's grapheme range contains it
            const editPosition = command.position ?? 0;
            let foundEditPage = false;
            
            // Sort pages by page_index to process in order
            const sortedPages = [...result.updatedPages].sort((a, b) => 
              (a.page_index ?? 0) - (b.page_index ?? 0)
            );
            
            for (const page of sortedPages) {
              if (typeof page.page_index === 'number') {
                // Simple heuristic: for single-char inserts, only the first page
                // in updatedPages (by page_index) needs re-rendering
                if (!foundEditPage) {
                  affectedPageIndices.add(page.page_index);
                  minAffectedPage = page.page_index;
                  foundEditPage = true;
                  debugLog('📱 [SharedInput] INSERT: Only rendering page', page.page_index, 'for edit at position', editPosition);
                  break; // Only render one page for insert
                }
              }
            }
            
            if (!foundEditPage && sortedPages.length > 0) {
              // Fallback: use first page
              const firstPage = sortedPages[0];
              if (typeof firstPage.page_index === 'number') {
                affectedPageIndices.add(firstPage.page_index);
                minAffectedPage = firstPage.page_index;
              }
            }
          } else if (isPageBreakInsert) {
            // 🔧 FIX (2026-01-16): Page break insertion - render ALL updated pages
            // This ensures the new page created by \f has its canvas populated
            debugLog('📱 [SharedInput] PAGE BREAK INSERT: Rendering all updated pages');
            result.updatedPages.forEach((page: any) => {
              if (typeof page.page_index === 'number') {
                affectedPageIndices.add(page.page_index);
                if (affectedPageIndices.size === 1 || page.page_index < minAffectedPage) {
                  minAffectedPage = page.page_index;
                }
              }
            });
            debugLog('📱 [SharedInput] PAGE BREAK: Affected pages:', Array.from(affectedPageIndices));
          } else {
            // DELETE: Use all pages in updatedPages (will be expanded below)
            result.updatedPages.forEach((page: any) => {
              if (typeof page.page_index === 'number') {
                affectedPageIndices.add(page.page_index);
                if (page.page_index < minAffectedPage || affectedPageIndices.size === 1) {
                  minAffectedPage = page.page_index;
                }
              }
            });
            debugLog('📱 [SharedInput] DELETE: Using all updatedPages as affected');
          }
        }
        
        // 🔥 DELETE FIX: For delete operations, ensure ALL pages from minAffectedPage onward are re-rendered
        // This is necessary because delete shifts content backward, making subsequent pages' slice boundaries stale
        // 🔥 CRITICAL: Use result.totalPages (the NEW page count), NOT totalRegisteredPages (the OLD count)
        // When a page break is deleted, totalRegisteredPages is stale (e.g., 4) but result.totalPages is correct (e.g., 3)
        const actualTotalPages = result.totalPages ?? totalRegisteredPages;
        if (isDeleteOperation && affectedPageIndices.size > 0) {
          for (let i = minAffectedPage; i < actualTotalPages; i++) {
            affectedPageIndices.add(i);
          }
          debugLog('📱 [SharedInput] DELETE operation: Re-rendering pages', minAffectedPage, 'to', actualTotalPages - 1);
        }
        
        // Check if we have registered canvas renderers for affected pages
        const hasRegisteredRenderers = canvasRenderersRef.current.size > 0;
        const canUseIncrementalRender = hasRegisteredRenderers && affectedPageIndices.size > 0;
        
        if (canUseIncrementalRender) {
          // 🚀 PHASE 4: Incremental render path (bypasses React)
          debugLog('📱 [SharedInput] 🚀 PHASE 4: Incremental render for pages:', Array.from(affectedPageIndices));
          
          const tRenderStart = performance.now();
          affectedPageIndices.forEach(pageIndex => {
            const renderer = canvasRenderersRef.current.get(pageIndex);
            if (renderer) {
              // Get the updated page data to pass to the renderer
              const pageData = result.updatedPages?.find((p: any) => p.page_index === pageIndex);
              renderer({
                clearCanvas: false, // Don't clear - incremental update
                spillFrontier: null, // spillFrontier not exposed at this level yet
                changedLines: pageData?.paragraphs?.flatMap((para: any) => para.lines || [])
              });
            }
          });
          const tRenderEnd = performance.now();
          WasmLogger.timing('Canvas incremental render', tRenderEnd - tRenderStart);
          
          // 🔥 CRITICAL FIX: Check if page count changed (increased OR decreased)
          // If increased: we MUST notify the parent to create a new Canvas for the new page.
          // If decreased: we MUST notify the parent to unmount the extra Canvas(es).
          // Without this, deleted pages remain visible with stale/ghost content.
          // 
          // 🔥 BUG FIX (2026-01-10): Compare against PREVIOUS TOTAL PAGES, not visible renderers!
          // canvasRenderersRef.current.size is the number of VISIBLE pages with renderers (e.g., 4)
          // result.totalPages is the TOTAL pages in document (e.g., 10)
          // Comparing these caused false "new page created" detection on EVERY keystroke,
          // triggering setRenderTrigger which unmounts/remounts all Canvas components.
          // The correct comparison is: previous total pages vs new total pages.
          const previousTotalPages = incrementalPagesRef.current?.totalPages ?? 0;
          const newPageCount = result.totalPages;
          const pageCountIncreased = newPageCount > previousTotalPages;
          const pageCountDecreased = newPageCount < previousTotalPages;
          
          if (pageCountIncreased) {
            // A new page was created - we need to trigger a React re-render
            // to create the new Canvas component for the new page.
            debugLog('📱 [SharedInput] 🚀 PHASE 4: New page created! Triggering React re-render for new Canvas');
            debugLog('📱 [SharedInput] Page count: ', previousTotalPages, ' → ', newPageCount);
            
            // Call onDocumentTextChange to notify parent of the new text (with page break)
            if (onDocumentTextChange) {
              onDocumentTextChange(result.newText);
            }
            
            // Also trigger a render to ensure the new Canvas is created
            setRenderTrigger(prev => prev + 1);
          } else if (pageCountDecreased) {
            // 🔥 CRITICAL FIX: A page was deleted - we need to trigger a React re-render
            // to UNMOUNT the extra Canvas component(s) for the deleted page(s).
            // Without this, the deleted page remains visible with ghost/stale content.
            debugLog('📱 [SharedInput] 🚀 PHASE 4: Page deleted! Triggering React re-render to unmount Canvas');
            debugLog('📱 [SharedInput] Page count: ', previousTotalPages, ' → ', newPageCount);
            
            // Call onDocumentTextChange to notify parent of the new text (without page break)
            if (onDocumentTextChange) {
              onDocumentTextChange(result.newText);
            }
            
            // Trigger a render to ensure the extra Canvas is unmounted
            setRenderTrigger(prev => prev + 1);
          } else {
            // 🚀 PHASE 4 FIX: Do NOT call setRenderTrigger here!
            // Calling setRenderTrigger causes React to re-render the entire tree,
            // which unmounts and remounts all Canvas components (destroying carets).
            // The incremental render callbacks already updated the canvases.
            // Caret position is updated via inputHandler.setCursorPosition() which
            // is called by the edit command handler, not via React state.
            // 
            // 🔥 CRITICAL FIX: We MUST call onDocumentTextChange to keep the parent's
            // text ref in sync for save operations. Without this, saving will use
            // stale text and lose content (e.g., Page 2 content gets lost).
            // The parent (EdgeDocsEditor.V3.tsx) updates documentStateRef.current.text
            // which is read by getDocumentState() during save.
            if (onDocumentTextChange) {
              onDocumentTextChange(result.newText);
            }
            const tTotal = performance.now() - t0;
            WasmLogger.timing('TOTAL handleEditCommand', tTotal);
            debugLog('📱 [SharedInput] 🚀 PHASE 4: Called onDocumentTextChange for save sync, skipped setRenderTrigger (incremental render complete)');
          }
        } else {
          // Fallback: Full re-render via React (when no renderers registered)
          debugLog('📱 [SharedInput] Triggering full render (no incremental renderers):', renderTrigger);
          setRenderTrigger(prev => {
            debugLog('📱 [SharedInput] New render trigger:', prev + 1);
            return prev + 1;
          });
          
          // Only notify parent when NOT using incremental render
          // (incremental render bypasses React, so parent doesn't need to know)
          if (onDocumentTextChange) {
            onDocumentTextChange(result.newText);
          }
        }
        
        // 🆕 Record edit for undo/redo
        // We need to record: position, deleted text, inserted text, cursor before/after
        const undoManager = getUndoManager();
        const position = command.position ?? 0;
        let undoDeletedText = '';
        let undoInsertedText = '';
        
        if (command.type === 'insert') {
          undoInsertedText = command.text ?? '';
        } else if (command.type === 'delete') {
          // Get the deleted text from the original document
          const deleteCount = command.deleteCount ?? 1;
          undoDeletedText = textBefore.slice(position, position + deleteCount);
        } else if (command.type === 'replace') {
          const deleteCount = command.deleteCount ?? 0;
          undoDeletedText = textBefore.slice(position, position + deleteCount);
          undoInsertedText = command.text ?? '';
        }
        
        // Cursor was at command.position before edit
        const cursorBefore = position;
        const cursorAfter = result.newCursorPosition;
        
        undoManager.recordEdit(position, undoDeletedText, undoInsertedText, cursorBefore, cursorAfter);
        updateUndoRedoState();
        
        return {
          success: true,
          newText: result.newText,
          newCursorPosition: result.newCursorPosition
        };
      } else {
        return {
          success: false,
          newText: currentDocumentTextRef.current || '',
          newCursorPosition: command.position,
          error: result.error
        };
      }
    } catch (error: any) {
      console.error('[SharedInput Option C] Edit command failed:', error);
      return {
        success: false,
        newText: currentDocumentTextRef.current || '',
        newCursorPosition: command.position,
        error: error.message
      };
    }
  }, [fontFamily, onDocumentTextChange, onPageCountChange]);
  
  // ============================================================================
  // Create the shared input handler
  // ============================================================================
  const inputHandler = useEdgeDocsInput(
    stableInputText,
    createTextEditingHandler({
      typesetterClientRef,
      currentDocumentTextRef,
      currentSnapshotRef,
      lastSelectionRef,
      incrementalPagesRef,
      lastRenderWasIncrementalRef,
      forceReinitRef,
      consecutiveWasmErrorsRef,
      lastLocalTextUpdateRef,
      overlaySettingsRef,
      setRenderTrigger,
      setStableInputText,
      setTypesetterClient: () => {}, // Not used at VirtualPages level
      setIsTypesetterReady: () => {}, // Not used at VirtualPages level
      documentState,
      fontFamily,
      onDocumentTextChange,
      debugLog,
      incrementalDebugLog: debugLog,
      renderOverlayRef,
      drawOverlaysRef,
      optionCEnabled: OPTION_C_ENABLED
    }),
    undefined, // maxLength
    OPTION_C_ENABLED ? {
      enabled: true,
      onEditCommand: handleEditCommand,
      onUndo: handleUndo,
      onRedo: handleRedo
    } : undefined
  );
  
  // 🆕 Update inputHandlerRef for UndoManager to use
  useEffect(() => {
    inputHandlerRef.current = inputHandler;
  }, [inputHandler]);

  // 🆕 2026-01-21: Keep formatting callback refs in sync with props
  // This is done synchronously (not in useEffect) to ensure refs are always current
  onToggleBoldRef.current = onToggleBold;
  onToggleItalicRef.current = onToggleItalic;
  // 🔧 FIX (2026-01-24): Keep selection change callback ref in sync
  onSelectionChangeRef.current = onSelectionChange;

  // 🆕 Wire undo/redo callbacks to input handlerafter initialization
  // This is needed because useCallback refs may not be stable at initial mount
  useEffect(() => {
    if (inputHandler && inputHandler.setUndoRedoCallbacks) {
      inputHandler.setUndoRedoCallbacks(handleUndo, handleRedo);
      debugLog('📱 [SharedInput] Wired undo/redo callbacks to input handler');
    }
  }, [inputHandler, handleUndo, handleRedo]);
  
  // 🆕 2026-01-21: Wire formatting callbacks to input handler for Ctrl+B/Ctrl+I keyboard shortcuts
  // IMPORTANT: We use refs to avoid re-running this effect when callback props change
  // The inputHandler stores the ref-based wrappers, which always call the current ref value
  useEffect(() => {
    if (inputHandler && inputHandler.setFormattingCallbacks) {
      // Wire wrapper functions that delegate to refs - this way callbacks stay current
      // without re-running the effect
      inputHandler.setFormattingCallbacks(
        onToggleBoldRef.current ? async () => onToggleBoldRef.current?.() : undefined,
        onToggleItalicRef.current ? async () => onToggleItalicRef.current?.() : undefined
      );
      console.log('🅱️ [SharedInputContext] Wired formatting callbacks to input handler (via refs)');
    }
  }, [inputHandler]); // Only re-run when inputHandler changes, NOT when callbacks change
  
  // 🔧 FIX (2026-01-24): Wire selection change callback to input handler
  // This enables toolbar operations to track the last non-collapsed selection
  // for applying formatting to selected text (e.g., font size changes)
  useEffect(() => {
    if (inputHandler && inputHandler.setOnSelectionChange) {
      // Wire wrapper function that delegates to ref - callback stays current without re-running effect
      inputHandler.setOnSelectionChange((selection) => {
        // Convert simple anchor/focus to the rich selection format expected by orchestrator
        if (onSelectionChangeRef.current) {
          const isCollapsed = selection.anchor === selection.focus;
          onSelectionChangeRef.current({
            anchor: { logical: { paraIndex: 0, charOffset: selection.anchor } },
            focus: { logical: { paraIndex: 0, charOffset: selection.focus } },
            isCollapsed
          });
        }
      });
      debugLog('🎯 [SharedInputContext] Wired selection change callback to input handler');
    }
  }, [inputHandler]); // Only re-run when inputHandler changes
  
  // ============================================================================
  // 🔧 FIX (2026-01-16): Wire cursor change broadcasting to all registered pages
  // This enables multi-page caret rendering after Ctrl+Enter (page break)
  // 🆕 Phase 4: O(log n) targeted caret routing when feature flag is enabled
  // ============================================================================
  useEffect(() => {
    if (!inputHandler) {
      return;
    }

    const useTargetedRouting = isTargetedCaretRoutingEnabled();
    debugLog(`🖍️ [SharedInput] Wiring cursor change broadcaster (targetedRouting=${useTargetedRouting})`);

    // 🆕 Deduplication: Track last logged cursor to prevent duplicate logs from React cascades
    let lastLoggedCursor: { index: number; timestamp: number } | null = null;
    const DEDUP_WINDOW_MS = 100; // Ignore duplicate cursor changes within 100ms

    // Set a single cursor change callback that either:
    // - Uses O(log n) targeted routing via HeightIndexManager (Phase 4)
    // - Falls back to O(n) broadcast to all pages (legacy behavior)
    inputHandler.setOnCursorChange((cursorIndex: number) => {
      const callbackCount = caretUpdateCallbacksRef.current.size;
      const client = typesetterClientRef.current;

      if (useTargetedRouting && client) {
        // 🆕 Phase 4: O(log n) targeted caret routing
        // Use HeightIndexManager to find the target page in O(log n) time
        const routingStart = performance.now();
        const caretResult = client.notifyCaretPosition(cursorIndex);
        const routingLatencyMs = performance.now() - routingStart;
        const targetPage = caretResult.page;

        if (targetPage >= 0) {
          // Only notify the target page and adjacent pages (for caret crossing)
          const pagesToNotify = new Set<number>();
          pagesToNotify.add(targetPage);

          // Also notify previous page (caret might be exiting from it)
          if (targetPage > 0 && caretResult.pageChanged) {
            pagesToNotify.add(targetPage - 1);
          }
          // Also notify next page (caret might be entering it)
          if (caretResult.pageChanged) {
            pagesToNotify.add(targetPage + 1);
          }

          // 🆕 Deduplication: Only log if cursor changed or outside dedup window
          const now = Date.now();
          const shouldLog = !lastLoggedCursor || 
            lastLoggedCursor.index !== cursorIndex ||
            (now - lastLoggedCursor.timestamp) > DEDUP_WINDOW_MS;

          if (shouldLog) {
            lastLoggedCursor = { index: cursorIndex, timestamp: now };
            
            // Cast to extended type for Phase 4 fields (gracefully handle missing fields)
            const extendedResult = caretResult as typeof caretResult & {
              yPosition?: number;
              heightIndexSize?: number;
              totalDocumentHeight?: number;
              binarySearchSteps?: number;
              crossedFormFeed?: boolean;
            };
            
            // Use null instead of undefined so JSON serialization preserves all fields
            WasmLogger.layout.caretRouting({
              architecture: 'TARGETED_CARET_ROUTING',
              phase: 'phase_4',
              
              // Cursor location
              cursorIndex,
              graphemeY: extendedResult.yPosition ?? null,
              paragraphIndex: caretResult.paragraphIndex,
              
              // Page routing result
              targetPage,
              pageChanged: caretResult.pageChanged,
              notifiedPages: Array.from(pagesToNotify),
              totalRegisteredPages: callbackCount,
              
              // Height index state (null until HeightIndexManager provides data)
              heightIndexSize: extendedResult.heightIndexSize ?? null,
              totalDocumentHeight: extendedResult.totalDocumentHeight ?? null,
              
              // Performance
              routingLatencyMs: parseFloat(routingLatencyMs.toFixed(2)),
              binarySearchSteps: extendedResult.binarySearchSteps ?? null,
              
              // Form feed detection
              formFeedCrossing: extendedResult.crossedFormFeed ?? null,
            });
          }

          pagesToNotify.forEach(pageIndex => {
            const callback = caretUpdateCallbacksRef.current.get(pageIndex);
            if (callback) {
              try {
                callback(cursorIndex);
              } catch (error) {
                console.error(`[SharedInput] Error in caret callback for page ${pageIndex}:`, error);
              }
            }
          });

          return; // Successfully used targeted routing
        }
        // Fall through to broadcast if page lookup failed
        debugLog(`🖍️ [SharedInput] Targeted routing failed (page=${targetPage}), falling back to broadcast`);
      }

      // Legacy O(n) broadcast: Notify ALL registered pages
      debugLog(`🖍️ [SharedInput] Broadcasting cursor change (${cursorIndex}) to ${callbackCount} pages`);
      caretUpdateCallbacksRef.current.forEach((callback, pageIndex) => {
        try {
          callback(cursorIndex);
        } catch (error) {
          console.error(`[SharedInput] Error in caret callback for page ${pageIndex}:`, error);
        }
      });
    });

    debugLog('🖍️ [SharedInput] ✅ Cursor change broadcaster wired');
  }, [inputHandler]);
  
  // ============================================================================
  // 🆕 OPTION 1 ARCHITECTURE: Wire ALL document lines at SharedInputContext level
  // This ensures the input handler always has complete document data for navigation,
  // matching how MS Word and Google Docs work.
  // ============================================================================
  useEffect(() => {
    if (!isReady || !typesetterClientRef.current || !inputHandler) {
      return;
    }
    
    debugLog('📱 [SharedInput] Wiring ALL document lines to input handler (Option 1 architecture)');
    InputLogger.context.layoutProvidersSet({ instanceId: instanceId.current });
    
    // Wire layout providers that return ALL document lines, not page-specific
    inputHandler.setLayoutProviders({
      // Return ALL lines from all cached paragraph snapshots
      getWasmLines: () => {
        const client = typesetterClientRef.current;
        if (!client) return [];
        return client.getAllCachedLines();
      },
      // Return ALL caret positions from all cached paragraph snapshots
      getCaretMap: () => {
        const client = typesetterClientRef.current;
        if (!client) return [];
        return client.getAllCachedCaretPositions();
      },
      // Grapheme-to-UTF16 mapping for the full document
      getGraphemeToUtf16: () => {
        const text = currentDocumentTextRef.current || '';
        return Array.from({ length: text.length }, (_, i) => i);
      },
      // Fallbacks (same as primary now, but kept for consistency)
      getAllCachedLines: () => {
        const client = typesetterClientRef.current;
        if (!client) return [];
        return client.getAllCachedLines();
      },
      getAllCachedCaretPositions: () => {
        const client = typesetterClientRef.current;
        if (!client) return [];
        return client.getAllCachedCaretPositions();
      }
    });
    
    debugLog('📱 [SharedInput] ✅ ALL document lines wired to input handler');
  }, [isReady, inputHandler, renderTrigger]); // Re-wire on renderTrigger to pick up new pagination data
  
  // ============================================================================
  // Setup keyboard listener at VirtualPages level
  // ============================================================================
  useEffect(() => {
    debugLog('📱 [SharedInput] Keyboard setup useEffect, isReady:', isReady, 'hasClient:', !!typesetterClientRef.current);
    if (!isReady) {
      debugLog('📱 [SharedInput] Not ready, skipping keyboard setup');
      return () => {};
    }
    
    debugLog('📱 [SharedInput] Setting up keyboard listener');
    InputLogger.keyboard.setup({ instanceId: instanceId.current });
    const cleanup = createKeyboardEventSetup({ inputHandler })();
    return () => {
      InputLogger.keyboard.teardown({ instanceId: instanceId.current });
      cleanup();
    };
  }, [inputHandler, isReady]);
  
  // ============================================================================
  // Context value
  // ============================================================================
  // ✅ ALWAYS pass inputHandler - it's usable even before WASM is ready
  // The handler manages local text state optimistically; WASM calls will wait
  // 🆕 Get all cached lines from typesetter client for cross-page navigation
  const getAllCachedLines = useCallback((): Line[] => {
    const client = typesetterClientRef.current;
    if (!client) return [];
    return client.getAllCachedLines();
  }, []);
  
  // 🆕 Get all cached caret positions from typesetter client for cross-page navigation
  const getAllCachedCaretPositions = useCallback((): CaretPosition[] => {
    const client = typesetterClientRef.current;
    if (!client) return [];
    return client.getAllCachedCaretPositions();
  }, []);
  
  // 🚀 PHASE 4: Register a Canvas component's incremental render callback
  const registerCanvasRenderer = useCallback((pageIndex: number, callback: IncrementalRenderCallback) => {
    canvasRenderersRef.current.set(pageIndex, callback);
    InputLogger.sharedInput.canvasRegistered(pageIndex, canvasRenderersRef.current.size);
  }, []);
  
  // 🚀 PHASE 4: Unregister a Canvas component when it unmounts
  const unregisterCanvasRenderer = useCallback((pageIndex: number) => {
    canvasRenderersRef.current.delete(pageIndex);
    InputLogger.sharedInput.canvasUnregistered(pageIndex, canvasRenderersRef.current.size);
  }, []);

  // 🆕 2026-01-20: Trigger all registered canvas renderers to redraw
  // Used after formatting changes (line spacing) to force canvas to re-render with new line positions
  const triggerCanvasRedraw = useCallback(() => {
    const rendererCount = canvasRenderersRef.current.size;

    // 📊 Log redraw trigger for formatting flow diagnosis
    InputLogger.sharedInput.formattingRedrawTriggered({
      registeredRenderers: rendererCount,
      pagesRedrawn: rendererCount,
    });

    // 🔴 Early warning if no renderers registered
    if (rendererCount === 0) {
      debugLog('📱 [SharedInput] triggerCanvasRedraw: WARNING - No canvas renderers registered!');
      debugLog('📱 [SharedInput] This means formatting changes will NOT be visible.');
      debugLog('📱 [SharedInput] Possible causes: Pages not mounted, or registerCanvasRenderer not called.');
    }

    let pagesRedrawn = 0;
    canvasRenderersRef.current.forEach((renderer, pgIdx) => {
      debugLog(`📱 [SharedInput] triggerCanvasRedraw: Calling renderer for page ${pgIdx}`);
      renderer({
        clearCanvas: true,  // Clear and redraw completely for formatting changes
        spillFrontier: null,
        changedLines: undefined  // Re-draw all lines
      });
      pagesRedrawn++;
    });

    debugLog(`📱 [SharedInput] triggerCanvasRedraw: Complete - ${pagesRedrawn}/${rendererCount} pages redrawn`);
  }, []);

  // 🆕 2026-01-21: Refresh lines in incrementalPagesRef from cached snapshots after formatting change
  // When Ctrl+B is pressed, the runs in snapshots change (font becomes bold) but incrementalPagesRef
  // still has old page data. This function updates the lines in existing pages with fresh snapshot data.
  const refreshIncrementalPagesFromSnapshots = useCallback(() => {
    const client = typesetterClientRef.current;
    if (!client) {
      debugLog('📱 [SharedInput] refreshIncrementalPagesFromSnapshots: No client');
      return;
    }

    const currentPages = incrementalPagesRef.current;
    if (!currentPages || !currentPages.pages || currentPages.pages.length === 0) {
      debugLog('📱 [SharedInput] refreshIncrementalPagesFromSnapshots: No existing pages');
      return;
    }

    // Get the fresh snapshots from TypesetterWorkerClient
    const freshSnapshots = client.getCachedParagraphSnapshots();
    if (!freshSnapshots || freshSnapshots.length === 0) {
      debugLog('📱 [SharedInput] refreshIncrementalPagesFromSnapshots: No cached snapshots');
      return;
    }

    debugLog(`📱 [SharedInput] refreshIncrementalPagesFromSnapshots: Refreshing ${currentPages.pages.length} pages from ${freshSnapshots.length} snapshots`);

    // For single-page documents with one paragraph, directly use the fresh snapshot lines
    // This is the common case for Ctrl+B on selected text
    if (currentPages.pages.length === 1 && freshSnapshots.length === 1) {
      const page = currentPages.pages[0] as any;
      const snapshot = freshSnapshots[0];
      
      if (snapshot.lines && snapshot.lines.length > 0) {
        // Update the page with fresh lines
        page.lines = snapshot.lines;
        debugLog(`📱 [SharedInput] refreshIncrementalPagesFromSnapshots: Updated page 0 with ${snapshot.lines.length} lines`);
      }
    } else {
      // Multi-page or multi-paragraph: Update each page's lines from matching snapshots
      // For now, collect all lines from all snapshots (works for page 0)
      const allLines: any[] = [];
      for (const snapshot of freshSnapshots) {
        if (snapshot.lines) {
          allLines.push(...snapshot.lines);
        }
      }
      
      // Update page 0 with all lines (simple approach for single-page docs)
      const page0 = currentPages.pages.find((p: any) => p.page_index === 0) as any;
      if (page0 && allLines.length > 0) {
        page0.lines = allLines;
        debugLog(`📱 [SharedInput] refreshIncrementalPagesFromSnapshots: Updated page 0 with ${allLines.length} lines (from ${freshSnapshots.length} snapshots)`);
      }
    }

    // Update timestamp so canvas knows this is fresh data
    incrementalPagesRef.current = {
      ...currentPages,
      timestamp: Date.now()
    };

    debugLog('📱 [SharedInput] refreshIncrementalPagesFromSnapshots: Complete');
  }, []);

  // 🔧 FIX (2026-01-16): Register a page's caret update callback for multi-page cursor support
  const registerCaretUpdateCallback = useCallback((pageIndex: number, callback: (cursorIndex: number) => void) => {
    caretUpdateCallbacksRef.current.set(pageIndex, callback);
    InputLogger.sharedInput.caretCallbackRegistered(pageIndex, caretUpdateCallbacksRef.current.size);
  }, []);
  
  // 🔧 FIX (2026-01-16): Unregister a page's caret update callback on unmount
  const unregisterCaretUpdateCallback = useCallback((pageIndex: number) => {
    caretUpdateCallbacksRef.current.delete(pageIndex);
    InputLogger.sharedInput.caretCallbackUnregistered(pageIndex, caretUpdateCallbacksRef.current.size);
  }, []);
  
  // � FIX (2026-01-16): Get authoritative page grapheme boundaries from incrementalPagesRef
  // This is the SOURCE OF TRUTH for page boundaries, used when local canvas snapshots may be stale
  const getPageGraphemeBounds = useCallback((pageIndex: number): { startGrapheme: number; endGrapheme: number } | null => {
    const pages = incrementalPagesRef.current?.pages;
    if (!pages || pages.length === 0) {
      return null;
    }

    // Find the page by index
    const page = pages.find((p: any) => p.page_index === pageIndex) as any;
    if (!page) {
      return null;
    }

    // 🔥 FIX: Handle BOTH page formats (NEW format with .lines OR OLD format with .paragraphs)
    // NEW format: { page_index, lines: [] } - lines are directly on the page
    // OLD format: { page_index, paragraphs: ParagraphSlice[] } - need to resolve slices

    if (page.lines && Array.isArray(page.lines) && page.lines.length > 0) {
      // NEW format: page has resolved lines
      const firstLine = page.lines[0];
      const lastLine = page.lines[page.lines.length - 1];

      const startGrapheme = firstLine.start_grapheme_index ?? 0;
      const endGrapheme = lastLine.end_grapheme_index ?? page.maxGrapheme ?? startGrapheme;

      debugLog(`📱 [SharedInput] getPageGraphemeBounds(${pageIndex}) [NEW FORMAT]:`, { startGrapheme, endGrapheme, linesCount: page.lines.length });

      return { startGrapheme, endGrapheme };
    }

    // OLD format: page has paragraph slices - need to get lines from cached snapshots
    // For now, return null to signal that bounds aren't available in this format
    // (The click handler will fall back to local canvas snapshot bounds)
    debugLog(`📱 [SharedInput] getPageGraphemeBounds(${pageIndex}) [OLD FORMAT]:`, 'Page has .paragraphs, not .lines - returning null');
    return null;
  }, []);
  
  // �🐛 DEBUG: Get last edit info for dashboard monitoring
  const getLastEditInfo = useCallback(() => {
    return lastEditInfoRef.current;
  }, []);
  
  // 🆕 Get document paragraph metadata for save operations
  const getDocumentParagraphs = useCallback(() => {
    const client = typesetterClientRef.current;
    if (!client) return [];
    return client.getDocumentParagraphs();
  }, []);

  // 🆕 Phase F (2026-01-21): Get paragraph metadata with formattingSpans from WASM
  // This async method fetches the authoritative paragraph data including character-level
  // formatting spans directly from WASM for save-to-database operations.
  const getDocumentParagraphsWithSpans = useCallback(async () => {
    const client = typesetterClientRef.current;
    if (!client) {
      console.warn('[SharedInput] getDocumentParagraphsWithSpans: No typesetter client');
      return { paragraphs: [], version: 0 };
    }
    try {
      const result = await client.getParagraphMetadata();
      console.log('[SharedInput] getDocumentParagraphsWithSpans result:', {
        paragraphCount: result.paragraphs.length,
        version: result.version,
        hasSpans: result.paragraphs.some((p: { formattingSpans?: unknown[] }) => p.formattingSpans?.length && p.formattingSpans.length > 0)
      });
      return result;
    } catch (error) {
      console.error('[SharedInput] getDocumentParagraphsWithSpans failed:', error);
      // Fallback to sync version without spans
      // Note: getDocumentParagraphs() returns textStart/textEnd, map to graphemeStart/graphemeEnd
      const fallbackParagraphs = client.getDocumentParagraphs();
      return {
        paragraphs: fallbackParagraphs.map((p: { id: string; type: string; version: number; textStart: number; textEnd: number }) => ({
          id: p.id,
          type: p.type,
          graphemeStart: p.textStart,
          graphemeEnd: p.textEnd,
          version: p.version,
          lineSpacing: 1.15,
          fontSizePx: 11,
          fontFamily: 'Arial',
          fontWeight: 400,
          fontStyle: 'normal',
          formattingSpans: [] as Array<{ id: string; start: number; end: number }>
        })),
        version: 0
      };
    }
  }, []);
  
  // 🔥 CRITICAL: Initialize incrementalPagesRef with initial pagination result
  // This ensures that the first edit has existing pages to merge with.
  // Without this, the first edit results in existing=0 and only the edited page is kept.
  const initializeIncrementalPages = useCallback((paginationResult: { pages: PageLayout[]; total_pages: number }) => {
    if (!paginationResult?.pages || paginationResult.pages.length === 0) {
      debugLog(`📱 [SharedInput] initializeIncrementalPages: No pages to initialize`);
      return;
    }

    // 🔥 FIX: Always initialize/update the ref, not just when null.
    // When the SharedInputProvider remounts (navigation, etc.), the ref is reset to null.
    // But the Canvas useEffect might not re-run if paginationResult didn't change.
    // By always updating, we ensure the ref is always in sync with the pagination result.
    const existingPageCount = incrementalPagesRef.current?.pages?.length ?? 0;
    const newPageCount = paginationResult.pages.length;
    const existingTotalPages = incrementalPagesRef.current?.totalPages ?? 0;
    const newTotalPages = paginationResult.total_pages;

    // 🔥 FIX (2026-01-16): NEVER overwrite with FEWER pages unless this is a DELETE operation.
    // When a page break is inserted (Ctrl+Enter), handleEditCommand correctly merges to 2 pages.
    // But then React re-renders and Canvas components remount with stale pagination data (1 page).
    // This stale data has newTotalPages=1 which incorrectly triggers "documentShrank" logic.
    //
    // The fix: Only allow "document shrank" overwrites when the existing ref is OLDER than
    // a reasonable threshold (e.g., 100ms). Edit operations update the ref immediately,
    // so stale Canvas mount data will have a newer timestamp but FEWER pages.
    const existingTimestamp = incrementalPagesRef.current?.timestamp ?? 0;
    const timeSinceLastUpdate = Date.now() - existingTimestamp;
    const isRecentUpdate = timeSinceLastUpdate < 500; // 500ms threshold

    // If we have MORE pages and the update was recent, don't let stale Canvas data overwrite it
    if (isRecentUpdate && existingTotalPages > newTotalPages) {
      debugLog(`📱 [SharedInput] initializeIncrementalPages: BLOCKED stale update (existing=${existingTotalPages} pages, new=${newTotalPages} pages, age=${timeSinceLastUpdate}ms)`);
      InputLogger.sharedInput.incrementalPagesBlocked('stale_update_detected', {
        existingTotalPages,
        newTotalPages,
        timeSinceLastUpdate,
        isRecentUpdate,
      });
      return;
    }

    // Update if:
    // 1. incrementalPagesRef is null (first init)
    // 2. We have equal or more pages (normal case)
    // 3. Document shrank AND update is not too recent (genuine DELETE, not stale remount)
    const documentShrank = newTotalPages < existingTotalPages && !isRecentUpdate;

    if (incrementalPagesRef.current === null || newPageCount >= existingPageCount || documentShrank) {
      incrementalPagesRef.current = {
        pages: paginationResult.pages,
        totalPages: paginationResult.total_pages,
        stoppedAtPage: null,
        timestamp: Date.now()
      };
      InputLogger.sharedInput.incrementalPagesInitialized(paginationResult.pages.length, paginationResult.total_pages, { documentShrank });
    } else {
      debugLog(`📱 [SharedInput] incrementalPagesRef already has more pages (${existingPageCount}), skipping update with ${newPageCount} pages`);
    }
  }, []);
  
  // 🔥 PERFORMANCE FIX (2026-01-10): Memoize context value to prevent unnecessary re-renders
  // Without memoization, every render of SharedInputProvider creates a new contextValue object,
  // which causes all context consumers to re-render and all useEffects depending on the context
  // to re-run (including the canvas register/unregister effect).
  const contextValue: SharedInputContextValue = React.useMemo(() => ({
    inputHandler,  // Always available, not gated by isReady
    typesetterClientRef,
    currentDocumentTextRef,
    currentSnapshotRef,
    incrementalPagesRef,
    lastRenderWasIncrementalRef,
    forceReinitRef,
    setRenderTrigger,
    renderTrigger,
    fontFamily,
    fontSizePx,
    // ✅ OPTION A: Single Source of Truth
    getAuthoritativeText,
    isWasmTextReady,
    markWasmTextReady,
    // 🆕 Cross-page navigation support
    getAllCachedLines,
    getAllCachedCaretPositions,
    // 🚀 PHASE 4: Canvas render registry
    registerCanvasRenderer,
    unregisterCanvasRenderer,
    triggerCanvasRedraw,  // 🆕 2026-01-20: For formatting changes
    refreshIncrementalPagesFromSnapshots,  // 🆕 2026-01-21: Refresh page lines after formatting
    // 🔥 CRITICAL: Initialize incrementalPagesRef with initial pagination
    initializeIncrementalPages,
    // 🐛 DEBUG: Last edit info for dashboard
    getLastEditInfo,
    // 🆕 Get document paragraph metadata for save
    getDocumentParagraphs,
    // 🆕 Phase F: Get paragraph metadata with formattingSpans (async, for save)
    getDocumentParagraphsWithSpans,
    // 🆕 Undo/Redo support
    undo: handleUndo,
    redo: handleRedo,
    canUndo,
    canRedo,
    // 🆕 Formatting undo/redo support
    registerFormattingUpdater,
    recordFormattingChange,
    // 🔧 FIX (2026-01-16): Multi-page caret support
    registerCaretUpdateCallback,
    unregisterCaretUpdateCallback,
    // 🔧 FIX (2026-01-16): Authoritative page boundaries
    getPageGraphemeBounds
  }), [
    inputHandler,
    typesetterClientRef,
    currentDocumentTextRef,
    currentSnapshotRef,
    incrementalPagesRef,
    lastRenderWasIncrementalRef,
    forceReinitRef,
    setRenderTrigger,
    // 🔥 PERFORMANCE FIX: Removed renderTrigger from deps - it changes on every edit
    // and would cause context identity churn, triggering canvas unregister/register storm.
    // Consumers that need renderTrigger should access it via the context value (which is
    // still updated), but the context identity won't change just because renderTrigger changed.
    // renderTrigger,  // REMOVED - causes identity churn
    fontFamily,
    fontSizePx,
    getAuthoritativeText,
    // 🔥 PERFORMANCE FIX: Removed isWasmTextReady from deps - callbacks now use refs
    // isWasmTextReady,  // REMOVED - causes identity churn
    markWasmTextReady,
    getAllCachedLines,
    getAllCachedCaretPositions,
    registerCanvasRenderer,
    unregisterCanvasRenderer,
    triggerCanvasRedraw,
    refreshIncrementalPagesFromSnapshots,
    initializeIncrementalPages,
    getLastEditInfo,
    getDocumentParagraphs,
    // 🆕 Undo/Redo
    handleUndo,
    handleRedo,
    canUndo,
    canRedo,
    // 🆕 Formatting undo/redo
    registerFormattingUpdater,
    recordFormattingChange,
    // 🔧 FIX (2026-01-16): Multi-page caret support
    registerCaretUpdateCallback,
    unregisterCaretUpdateCallback
  ]);
  
  return (
    <SharedInputContext.Provider value={contextValue}>
      {children}
    </SharedInputContext.Provider>
  );
};
